/*
QC & Review automatic fixes.

Maps the structured issue codes produced by validateEventGroup()
(@cclib/lib/styleGuideTests.js) onto the existing cc-lib correction engines,
and applies them at three scopes:

  - a single error on a single event (codes + eventIds both set)
  - every fixable error on one event   (eventIds set, codes null)
  - selected error types across the whole group (codes set, eventIds null)

The caller is responsible for history snapshots and re-running the review.
*/

import correctReadingSpeed from "@cclib/functions/eventGroups/correctReadingSpeed.js";
import autoFormat from "@cclib/functions/eventGroups/autoFormat.js";
import convertToPlainText from "@cclib/functions/quill/convertToPlainText.js";

const EPSILON = 0.0005;

/* Fixer definitions in the order they must run. Text cleanup and blank-event
   removal go first (they don't move timecodes), auto format next (it can
   split events, changing indexes), then the timing fixers. */
const FIXERS = [
    {
        name: "textCleanup",
        codes: ["leadingTrailingSpace", "whitespace", "blankLines"]
    },
    {
        name: "removeBlank",
        codes: ["emptyText"],
        destructive: true
    },
    {
        name: "autoFormat",
        codes: ["maxLines", "maxCharsPerLine", "totalMaxChars", "fitOneLine"]
    },
    {
        name: "overlap",
        codes: ["overlap"]
    },
    {
        name: "readingSpeed",
        codes: ["cpsMax", "wpm", "durationMin", "durationMax"]
    },
    {
        name: "frameGap",
        codes: ["gapTooSmall", "gapTolerance", "gapTooLarge"]
    }
];

export const CODE_TO_FIXER = {};
FIXERS.forEach(fixer => {
    fixer.codes.forEach(code => {
        CODE_TO_FIXER[code] = fixer.name;
    });
});

export const DESTRUCTIVE_CODES = new Set(
    FIXERS.filter(fixer => fixer.destructive).flatMap(fixer => fixer.codes)
);

export function isFixableCode(code) {
    return Boolean(CODE_TO_FIXER[code]);
}

/**
 * Counts fixable issues per code across a validation result.
 * Used to build the Fix All checkbox panel.
 * @param {Array} errors - Output of validateEventGroup
 * @returns {Map<string, {count: number, category: string}>}
 */
export function collectFixableCodes(errors) {
    const counts = new Map();

    (errors || []).forEach(error => {
        (error.issues || []).forEach(issue => {
            if (!isFixableCode(issue.code)) {
                return;
            }

            const entry = counts.get(issue.code) || { count: 0, category: issue.category };
            entry.count++;
            counts.set(issue.code, entry);
        });
    });

    return counts;
}

function firstFinite(...values) {
    for (const value of values) {
        const num = parseFloat(value);
        if (Number.isFinite(num)) {
            return num;
        }
    }
    return undefined;
}

function hasTime(value) {
    return typeof value === "number" && !isNaN(value);
}

function cleanText(html, codes) {
    let text = html || "";

    if (codes.has("blankLines")) {
        text = text.replace(/<p\b[^>]*>\s*<br\s*\/?>\s*<\/p>/gi, "");
    }
    if (codes.has("leadingTrailingSpace")) {
        text = text.replace(/(<p\b[^>]*>)[\s\t]+/gi, "$1").replace(/[\s\t]+(<\/p>)/gi, "$1");
    }
    if (codes.has("whitespace")) {
        text = text.replace(/\s{2,}/g, " ");
    }

    return text;
}

/* Mirrors fixOverlap.js for a single pair: keep the event with the longer
   remaining duration intact and trim the other one. */
function fixOverlapPair(events, index, gapSec) {
    const current = events[index];
    const next = events[index + 1];

    if (!next || !hasTime(current.end) || !hasTime(next.start)) {
        return false;
    }

    if (next.start >= current.end + gapSec - EPSILON) {
        return false;
    }

    const trimmedEnd = next.start - gapSec;
    const delayedStart = current.end + gapSec;
    const currentDuration = trimmedEnd - current.start;
    const nextDuration = next.end - delayedStart;

    if (nextDuration > currentDuration) {
        next.start = delayedStart;
    } else {
        current.end = trimmedEnd;
    }

    return true;
}

/* Enforces the style guide frame gap on one pair. Prefers trimming the
   earlier event's end (no-cascade); only delays the next start when the
   trim would consume the earlier event entirely. */
function fixGapPair(events, index, opts) {
    const current = events[index];
    const next = events[index + 1];

    if (!next || !hasTime(current.end) || !hasTime(next.start)) {
        return false;
    }

    const frameSec = 1 / Math.round(opts.frameRate);
    const gapFrames = Math.round((next.start - current.end) / frameSec);

    if (gapFrames > opts.maxGapFrames) {
        // Close an oversized gap by extending the earlier event, capped at maxDuration
        const desiredEnd = next.start - opts.maxGapFrames * frameSec;
        const cappedEnd = Math.min(desiredEnd, current.start + opts.maxDuration);
        if (cappedEnd > current.end + EPSILON) {
            current.end = cappedEnd;
            return true;
        }
        return false;
    }

    const desiredEnd = next.start - opts.minGapFrames * frameSec;
    const floorEnd = current.start + frameSec;

    if (desiredEnd >= floorEnd - EPSILON) {
        if (Math.abs(desiredEnd - current.end) > EPSILON) {
            current.end = desiredEnd;
            return true;
        }
        return false;
    }

    const desiredStart = current.end + opts.minGapFrames * frameSec;
    const ceilStart = next.end - frameSec;
    const newStart = Math.min(desiredStart, ceilStart);

    if (newStart > next.start + EPSILON) {
        next.start = newStart;
        return true;
    }

    return false;
}

/**
 * Applies automatic fixes to an event group, in place.
 *
 * @param {Object} eventGroup - The live event group (mutated)
 * @param {Array} errors - Current validateEventGroup output
 * @param {Object} options
 * @param {Array<string>|null} options.codes - Issue codes to fix (null = all fixable)
 * @param {Array<string>|null} options.eventIds - Restrict to these event ids (null = all)
 * @param {Object} options.styleGuide - The style guide the review ran against
 * @param {number} options.frameRate
 * @param {boolean} options.excludeSpaces - CPS counting mode
 * @param {boolean} [options.allowMerge=true] - Let reading-speed correction merge events
 * @param {string} [options.failureNote] - Note applied to events that could not be corrected
 * @returns {{changed: boolean, summary: Object}}
 */
export function applyQcFixes(eventGroup, errors, options = {}) {
    const {
        codes = null,
        eventIds = null,
        styleGuide = {},
        frameRate,
        excludeSpaces = false,
        allowMerge = true,
        failureNote = "Reading speed too high - could not be corrected automatically"
    } = options;

    const codeSet = codes ? new Set(codes) : null;
    const idSet = eventIds ? new Set(eventIds) : null;

    /* Bucket the flagged events by fixer */
    const buckets = new Map();
    (errors || []).forEach(error => {
        if (idSet && !idSet.has(error.id)) {
            return;
        }

        (error.issues || []).forEach(issue => {
            const fixerName = CODE_TO_FIXER[issue.code];
            if (!fixerName || (codeSet && !codeSet.has(issue.code))) {
                return;
            }

            let bucket = buckets.get(fixerName);
            if (!bucket) {
                bucket = { ids: new Set(), codes: new Set() };
                buckets.set(fixerName, bucket);
            }
            bucket.ids.add(error.id);
            bucket.codes.add(issue.code);
        });
    });

    const summary = {
        textCleanup: 0,
        removedEvents: 0,
        autoFormat: 0,
        overlap: 0,
        readingSpeed: null,
        frameGap: 0
    };

    const group = eventGroup;

    const maxLines = firstFinite(styleGuide.maxLines, group.maxLines, 2);
    const maxChars = firstFinite(styleGuide.maxChars, group.maxChars, 32);
    const minDuration = firstFinite(styleGuide.minDuration, group.minDuration, 1);
    const maxDuration = firstFinite(styleGuide.maxDuration, group.maxDuration, 12);
    const maxCps = firstFinite(styleGuide.maxCps, group.maxCps, 25);
    const minGapFrames = Math.max(0, Math.round(firstFinite(styleGuide.minEventGap, 2)));
    const maxGapFrames = Math.round(firstFinite(styleGuide.maxEventGap, Infinity));

    /* 1. Text cleanup (no timing changes) */
    const textBucket = buckets.get("textCleanup");
    if (textBucket) {
        group.events.forEach(event => {
            if (!textBucket.ids.has(event.id)) {
                return;
            }

            const cleaned = cleanText(event.text, textBucket.codes);
            if (cleaned !== event.text) {
                event.text = cleaned;
                event.rendered = false;
                summary.textCleanup++;
            }
        });
    }

    /* 2. Remove blank events (destructive - only when explicitly requested) */
    const blankBucket = buckets.get("removeBlank");
    if (blankBucket) {
        const before = group.events.length;
        group.events = group.events.filter(event => {
            if (!blankBucket.ids.has(event.id)) {
                return true;
            }
            return convertToPlainText(event.text).trim().length > 0;
        });
        summary.removedEvents = before - group.events.length;
    }

    /* 3. Auto format (may split events, so run before the timing fixers) */
    const formatBucket = buckets.get("autoFormat");
    if (formatBucket) {
        const indexes = [];
        group.events.forEach((event, index) => {
            if (formatBucket.ids.has(event.id)) {
                indexes.push(index);
            }
        });

        if (indexes.length > 0) {
            autoFormat(group, maxLines, maxChars, minDuration, true, indexes);
            summary.autoFormat = indexes.length;
        }
    }

    /* 4. Overlaps (pair-wise so untouched events keep their timing) */
    const overlapBucket = buckets.get("overlap");
    if (overlapBucket) {
        const gapSec = minGapFrames / Math.round(frameRate);
        overlapBucket.ids.forEach(id => {
            const index = group.events.findIndex(event => event.id === id);
            if (index > -1 && fixOverlapPair(group.events, index, gapSec)) {
                summary.overlap++;
            }
        });
    }

    /* 5. Reading speed / duration correction */
    const speedBucket = buckets.get("readingSpeed");
    if (speedBucket) {
        const { stats } = correctReadingSpeed(group, {
            frameRate,
            maxCps,
            minDuration,
            maxDuration,
            minFrameGap: minGapFrames,
            allowMerge,
            maxChars,
            maxLines,
            excludeSpaces,
            markFailures: true,
            failureNote,
            targetIds: [...speedBucket.ids]
        });
        summary.readingSpeed = stats;
    }

    /* 6. Frame gaps */
    const gapBucket = buckets.get("frameGap");
    if (gapBucket) {
        const gapOpts = { frameRate, minGapFrames, maxGapFrames, maxDuration };
        gapBucket.ids.forEach(id => {
            const index = group.events.findIndex(event => event.id === id);
            if (index > -1 && fixGapPair(group.events, index, gapOpts)) {
                summary.frameGap++;
            }
        });
    }

    const changed =
        summary.textCleanup > 0 ||
        summary.removedEvents > 0 ||
        summary.autoFormat > 0 ||
        summary.overlap > 0 ||
        summary.frameGap > 0 ||
        (summary.readingSpeed &&
            (summary.readingSpeed.extended > 0 ||
                summary.readingSpeed.merged > 0 ||
                summary.readingSpeed.failed > 0));

    return { changed: Boolean(changed), summary };
}
